<template>
  <div class="hotel-wise-booking-report">
    <!-- Filters -->
    <b-card class="mb-4">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="fas fa-filter mr-2"></i>Filters
        </h5>
        <div>
          <b-button variant="outline-primary" @click="exportReport('csv')" :disabled="loading" class="mr-2">
            <i class="fas fa-download mr-2"></i>Export CSV
          </b-button>
          <b-button variant="outline-secondary" @click="exportReport('pdf')" :disabled="loading">
            <i class="fas fa-file-pdf mr-2"></i>Export PDF
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-form @submit.prevent="fetchReport">
          <b-row>
            <b-col md="4">
              <b-form-group label="Date From:">
                <b-form-datepicker v-model="filters.date_from" />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Date To:">
                <b-form-datepicker v-model="filters.date_to" />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Group By:">
                <b-form-select v-model="filters.group_by" :options="groupByOptions" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col class="d-flex align-items-end">
              <b-button type="submit" variant="primary" :disabled="loading" class="mr-2">
                <i class="fas fa-search mr-2"></i>Apply Filters
              </b-button>
              <b-button variant="outline-secondary" @click="resetFilters" :disabled="loading">
                <i class="fas fa-times mr-2"></i>Clear
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card-body>
    </b-card>

    <!-- Results -->
    <b-card>
      <b-card-header>
        <h5 class="mb-0">
          <i class="fas fa-chart-bar mr-2"></i>Hotel Performance Results
        </h5>
      </b-card-header>
      <b-card-body>
        <div v-if="loading" class="text-center p-4">
          <b-spinner variant="primary"></b-spinner>
          <p class="mt-2 mb-0">Loading hotel performance data...</p>
        </div>

        <div v-else-if="!reportData || reportData.length === 0" class="text-center p-4">
          <p class="mb-0">No data found for the selected criteria.</p>
        </div>
        <div >
          <div v-for="hotel in reportData" :key="hotel.hotel_id" class="mb-4">
            <h6 class="font-weight-bold">{{ hotel.hotel_name }}</h6>
            <div class="row mb-2">
              <div class="col-md-6">
                <strong>Total Bookings:</strong> {{ hotel.total_bookings }}
              </div>
              <div class="col-md-6">
                <strong>Total Revenue:</strong> ${{ Number(hotel.total_revenue).toFixed(2) }}
              </div>
            </div>

            <b-table
              :items="hotel.periods"
              :fields="periodFields"
              small
              striped
              class="mb-0"
            >
              <template #cell(total_revenue)="data">
                ${{ Number(data.item.total_revenue).toFixed(2) }}
              </template>
            </b-table>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { request } from '@/Util/Request';

export default {
  name: 'HotelWiseBookingReport',
  data() {
    return {
      loading: false,
      filters: {
        date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        date_to: new Date().toISOString().split('T')[0],
        group_by: 'day'
      },
      reportData: [],
      groupByOptions: [
        { value: 'day', text: 'Daily' },
        { value: 'week', text: 'Weekly' },
        { value: 'month', text: 'Monthly' }
      ],
      periodFields: [
        { key: 'period', label: 'Period' },
        { key: 'booking_count', label: 'Bookings' },
        { key: 'total_revenue', label: 'Revenue' }
      ]
    };
  },
  async mounted() {
    await this.fetchReport();
  },
  methods: {
    async fetchReport() {
      try {
        this.loading = true;
        const response = await request({
          method: 'get',
          url: '/hotel-reports/hotel-wise-bookings',
          params: this.filters
        });

        this.reportData = response;
      } catch (error) {
        console.error('Error fetching hotel-wise booking report:', error);
        this.$bvToast.toast('Failed to load hotel-wise booking report', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.filters = {
        date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        date_to: new Date().toISOString().split('T')[0],
        group_by: 'day'
      };
        this.fetchReport();
    },

    // Export methods
    async exportReport(format) {
      try {
        // Set loader through Vuex
          this.loading = true;

        const params = {
          ...this.reportFilters,
          format: format
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        const response = await request({
          method: 'get',
          url: '/hotel-reports/hotel-wise-bookings-export',
          params: params,
          responseType: 'blob'
        });

        // Create download link
        const mimeType = format === 'csv'
            ? 'text/csv'
            : 'application/pdf';

        const blob = new Blob([response], { type: mimeType });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `hotel_wise_booking_report.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$bvToast.toast(`Report exported successfully as ${format.toUpperCase()}`, {
            title: 'Success',
            variant: 'success',
            solid: true
        });
      } catch (error) {
        console.error('Export failed:', error);
        this.$bvToast.toast('Failed to export report', {
            title: 'Error',
            variant: 'danger',
            solid: true
        });
      } finally {
          this.loading = false;
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    }
  }
};
</script>

<style scoped>
/* .hotel-wise-booking-report {
  padding: 20px;
} */

.text-success {
  color: #28a745 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-primary {
  color: #007bff !important;
}
</style>
